import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { applicationConfiguration } from 'app/modules/shared'
import { setCoralogixUser, initCoralogix } from './coralogix'
import { AuthContext } from 'app/modules/auth/context'
import { initOtel, shutdownOtel } from './otel'
import { VersionInfo } from 'app/modules/admin/features/settings/appearance/types'
import { settingsService } from 'app/modules/admin/services/settings.service'
import { logger } from 'app/components/logger'

export const monitoringIgnoreUrls = [
  /googleapis/,
  /google-analytics/,
  /coralogix/,
  /gtm/,
  /\/v1\/traces/,
  /cookielaw/,
  /amplitude/,
]

const monitoringContext = createContext({})

// Handle client-side monitoring
export const WithMonitoringContext = ({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element | null => {
  const config = useContext(applicationConfiguration)
  const auth = useContext(AuthContext)
  const [version, setVersion] = useState<VersionInfo['webservicefront_version']>('')

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await settingsService.getVersionInfo()
        setVersion(response.webservicefront_version)
      } catch (err) {
        logger.warn('Failed to fetch version info')
      }
    }

    fetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // This context does not provide any data
  const provided = useMemo(() => ({}), [])

  // Setup or shutdown coralogix
  useEffect(() => {
    initCoralogix(config?.coralogix, version)
    setCoralogixUser(auth.user)
  }, [config?.coralogix, version])

  // Setup or shutdown OpenTelemetry
  useEffect(() => {
    initOtel(config?.openTelemetry, version)
    return () => shutdownOtel()
  }, [config?.openTelemetry, version])

  // Set Coralogix user context when user changes
  useEffect(() => {
    setCoralogixUser(auth.user)
  }, [auth.user])

  return <monitoringContext.Provider value={provided}>{children}</monitoringContext.Provider>
}
